/**
 * NodeDirectoryPanel — tělo widgetu „Uzly ACM" (acm-self.node-directory).
 *
 * Všechny ACM uzly s odkazem na dashboard (nový tab) a MCP adresou ke zkopírování,
 * ať si nikdo nemusí pamatovat hashe v URL. Ruční záznamy + živý objev přes
 * `acm-self.node_directory`; úpravy přes `acm-self.node_directory_set` (platform admin).
 * Chrome (karta, hlavička) kreslí WidgetShell — tady jen obsah.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ExternalLink, Pencil, Plus, RefreshCw, Server, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Label,
  Modal,
  SearchInput,
  StatusDot,
  Textarea,
  UrlRow,
} from './ui';
import { callWidgetTool } from '../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../widgets/types';
import { themedConfirm } from '../lib/themed-dialogs';
import {
  filterDirectoryRows,
  type NodeDirectoryResult,
  type NodeDirectoryRow,
  onlineStatus,
} from '../lib/node-directory';

interface FormState {
  id: string | null;
  label: string;
  environment: string;
  customer: string;
  dashboard_url: string;
  mcp_url: string;
  node_id: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  label: '',
  environment: '',
  customer: '',
  dashboard_url: '',
  mcp_url: '',
  node_id: '',
  notes: '',
};

function formFromRow(r: NodeDirectoryRow): FormState {
  return {
    id: r.id,
    label: r.label,
    environment: r.environment ?? '',
    customer: r.customer ?? '',
    dashboard_url: r.dashboard_url ?? r.discovered_dashboard_url ?? '',
    mcp_url: r.mcp_url ?? '',
    node_id: r.node_id ?? '',
    notes: r.notes ?? '',
  };
}

const QUERY_KEY = 'acm-self-node-directory';

export function NodeDirectoryPanel({ scope, viewport }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [form, setForm] = useState<FormState | null>(null);
  const sc: EffectiveScope = scope ?? { node: null, identity: null };
  const key = [QUERY_KEY, sc.node ?? 'local'];

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: key,
    queryFn: () =>
      callWidgetTool<NodeDirectoryResult>('acm-self.node_directory', {}, sc, { silent: true }),
    staleTime: 30_000,
    refetchInterval: 120_000,
  });

  const refresh = useMutation({
    mutationFn: () =>
      callWidgetTool<NodeDirectoryResult>('acm-self.node_directory', { refresh: true }, sc, {
        silent: true,
      }),
    onSuccess: (res) => qc.setQueryData(key, res),
    onError: (e: Error) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: async (f: FormState) => {
      const r = await callWidgetTool<{ ok: boolean; error?: string }>(
        'acm-self.node_directory_set',
        {
          ...(f.id ? { id: f.id } : {}),
          label: f.label,
          environment: f.environment,
          customer: f.customer,
          dashboard_url: f.dashboard_url,
          mcp_url: f.mcp_url,
          node_id: f.node_id,
          notes: f.notes,
        },
        sc,
      );
      if (!r?.ok) throw new Error(r?.error || t('nodeDirectory.saveFailed', 'Uložení selhalo'));
      return r;
    },
    onSuccess: () => {
      toast.success(t('nodeDirectory.saved', 'Uzel uložen'));
      setForm(null);
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await callWidgetTool<{ ok: boolean; error?: string }>(
        'acm-self.node_directory_set',
        { id, delete: true },
        sc,
      );
      if (!r?.ok) throw new Error(r?.error || t('nodeDirectory.deleteFailed', 'Smazání selhalo'));
      return r;
    },
    onSuccess: () => {
      toast.success(t('nodeDirectory.deleted', 'Záznam smazán'));
      setForm(null);
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const askDelete = async (f: FormState) => {
    if (!f.id) return;
    const ok = await themedConfirm({
      title: t('nodeDirectory.deleteTitle', 'Smazat záznam'),
      description: t('nodeDirectory.deleteConfirm', 'Smazat „{{name}}“ z adresáře uzlů?', {
        name: f.label,
      }),
    });
    if (ok) remove.mutate(f.id);
  };

  const rows = data?.rows ?? [];
  const shown = filterDirectoryRows(rows, query);
  const counts = data?.counts;
  const unreachable = data?.discovery?.unreachable ?? [];
  const compact = viewport === 'mobile';

  const sourceLabel = (s: NodeDirectoryRow['source']) =>
    s === 'mesh'
      ? t('nodeDirectory.sourceMesh', 'mesh')
      : s === 'both'
      ? t('nodeDirectory.sourceBoth', 'mesh + ručně')
      : t('nodeDirectory.sourceManual', 'ručně');

  const statusLabel = (r: NodeDirectoryRow) =>
    r.online === true
      ? t('nodeDirectory.online', 'online')
      : r.online === false
      ? t('nodeDirectory.offline', 'offline')
      : t('nodeDirectory.unknown', 'stav neznámý');

  const set = (patch: Partial<FormState>) => setForm((f) => (f ? { ...f, ...patch } : f));

  if (data && data.ok === false) {
    return (
      <div className='p-4 text-sm text-red-300'>
        {data.error || t('nodeDirectory.loadFailed', 'Adresář uzlů se nepodařilo načíst')}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3 p-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <SearchInput
          value={query}
          onChange={setQuery}
          size='sm'
          className='min-w-[10rem] flex-1'
          placeholder={t('nodeDirectory.search', 'Hledat uzel, zákazníka, doménu…')}
          resultCount={query ? shown.length : undefined}
          totalCount={query ? rows.length : undefined}
        />
        <Button
          size='sm'
          variant='ghost'
          icon={RefreshCw}
          loading={refresh.isPending}
          onClick={() => refresh.mutate()}
          title={t('nodeDirectory.refreshHint', 'Znovu se zeptat uzlů (obejde 60s cache)')}
        >
          {compact ? null : t('nodeDirectory.refresh', 'Obnovit')}
        </Button>
        <Button size='sm' variant='primary' icon={Plus} onClick={() => setForm({ ...EMPTY_FORM })}>
          {t('nodeDirectory.add', 'Přidat uzel')}
        </Button>
      </div>

      {counts && (
        <div className='text-[11px] text-gray-500'>
          {t('nodeDirectory.summary', '{{total}} uzlů · {{online}} online · {{mesh}} jen z meshe', {
            total: counts.total,
            online: counts.online,
            mesh: counts.mesh,
          })}
          {unreachable.length > 0 && (
            <span className='ml-1 text-amber-400/80'>
              {t('nodeDirectory.unreachable', '· neodpověděly: {{nodes}}', {
                nodes: unreachable.join(', '),
              })}
            </span>
          )}
        </div>
      )}

      {isLoading && (
        <div className='py-6 text-center text-xs text-gray-500'>
          {t('nodeDirectory.loading', 'Načítám uzly…')}
        </div>
      )}
      {error && !data && <div className='text-sm text-red-300'>{(error as Error).message}</div>}

      {!isLoading && !error && rows.length === 0 && (
        <EmptyState
          icon={Server}
          title={t('nodeDirectory.emptyTitle', 'Adresář uzlů je prázdný')}
          description={t(
            'nodeDirectory.emptyHint',
            'Přidej uzel ručně — uzly mimo mesh (zákaznické, jiný cluster) se samy neobjeví.',
          )}
          action={
            <Button
              size='sm'
              variant='primary'
              icon={Plus}
              onClick={() => setForm({ ...EMPTY_FORM })}
            >
              {t('nodeDirectory.add', 'Přidat uzel')}
            </Button>
          }
        />
      )}

      {rows.length > 0 && shown.length === 0 && (
        <div className='py-4 text-center text-xs text-gray-500'>
          {t('nodeDirectory.noMatch', 'Žádný uzel neodpovídá hledání')}
        </div>
      )}

      {shown.length > 0 && (
        <ul className={`divide-y divide-gray-800/60 ${isFetching ? 'opacity-80' : ''}`}>
          {shown.map((r) => (
            <li key={r.id ?? r.node_id ?? r.label} className='flex flex-col gap-1.5 py-2.5'>
              <div className='flex flex-wrap items-center gap-2'>
                <span title={statusLabel(r)}>
                  <StatusDot status={onlineStatus(r)} />
                </span>
                {r.dashboard_url
                  ? (
                    <a
                      href={r.dashboard_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-1 text-sm font-semibold text-gray-100 hover:text-plex'
                      title={t('nodeDirectory.openDashboard', 'Otevřít dashboard v novém panelu')}
                    >
                      {r.label}
                      <ExternalLink size={12} className='text-gray-500' />
                    </a>
                  )
                  : <span className='text-sm font-semibold text-gray-300'>{r.label}</span>}
                {r.local && (
                  <Badge variant='purple'>{t('nodeDirectory.thisNode', 'tento uzel')}</Badge>
                )}
                {r.environment && (
                  <Badge variant={/prod/i.test(r.environment) ? 'warning' : 'info'}>
                    {r.environment}
                  </Badge>
                )}
                {r.customer && <Badge variant='default'>{r.customer}</Badge>}
                <span className='text-[10px] uppercase tracking-wide text-gray-600'>
                  {sourceLabel(r.source)}
                </span>
                <Button
                  size='icon'
                  variant='ghost'
                  className='ml-auto'
                  icon={r.id ? Pencil : Plus}
                  onClick={() =>
                    setForm(formFromRow(r))}
                  title={r.id
                    ? t('nodeDirectory.edit', 'Upravit uzel')
                    : t('nodeDirectory.addFromMesh', 'Přidat do adresáře')}
                  aria-label={r.id
                    ? t('nodeDirectory.edit', 'Upravit uzel')
                    : t('nodeDirectory.addFromMesh', 'Přidat do adresáře')}
                />
              </div>
              <div className='flex flex-col gap-1 pl-4'>
                <UrlRow
                  label={t('nodeDirectory.dashboard', 'Dashboard')}
                  url={r.dashboard_url}
                  isPublic
                />
                <UrlRow label={t('nodeDirectory.mcp', 'MCP')} url={r.mcp_url} />
                {!r.dashboard_url && (
                  <span className='text-xs text-gray-600'>
                    {t('nodeDirectory.noUrl', 'Veřejná adresa dashboardu není známá')}
                  </span>
                )}
                {r.discovered_dashboard_url && (
                  <span className='inline-flex items-center gap-1 text-xs text-amber-300/90'>
                    <AlertTriangle size={12} />
                    {t('nodeDirectory.urlMismatch', 'Uzel hlásí jinou adresu: {{url}}', {
                      url: r.discovered_dashboard_url,
                    })}
                  </span>
                )}
                {r.notes && <span className='text-xs text-gray-500'>{r.notes}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id
          ? t('nodeDirectory.editTitle', 'Upravit uzel')
          : t('nodeDirectory.addTitle', 'Přidat uzel')}
        footer={form && (
          <div className='flex w-full items-center gap-2'>
            {form.id && (
              <Button
                variant='danger'
                size='sm'
                icon={Trash2}
                loading={remove.isPending}
                onClick={() => askDelete(form)}
              >
                {t('common.delete', 'Smazat')}
              </Button>
            )}
            <Button variant='ghost' size='sm' className='ml-auto' onClick={() => setForm(null)}>
              {t('common.cancel', 'Zrušit')}
            </Button>
            <Button
              variant='primary'
              size='sm'
              loading={save.isPending}
              disabled={!form.label.trim() || !form.dashboard_url.trim()}
              onClick={() => save.mutate(form)}
            >
              {t('common.save', 'Uložit')}
            </Button>
          </div>
        )}
      >
        {form && (
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <div className='sm:col-span-2'>
              <Label htmlFor='nd-label'>{t('nodeDirectory.fieldLabel', 'Název uzlu')}</Label>
              <Input
                id='nd-label'
                value={form.label}
                placeholder='MAXACM000'
                onChange={(e) => set({ label: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor='nd-env'>{t('nodeDirectory.fieldEnvironment', 'Prostředí')}</Label>
              <Input
                id='nd-env'
                value={form.environment}
                placeholder='prod'
                onChange={(e) => set({ environment: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor='nd-customer'>{t('nodeDirectory.fieldCustomer', 'Zákazník')}</Label>
              <Input
                id='nd-customer'
                value={form.customer}
                onChange={(e) => set({ customer: e.target.value })}
              />
            </div>
            <div className='sm:col-span-2'>
              <Label htmlFor='nd-dash'>
                {t('nodeDirectory.fieldDashboard', 'URL dashboardu (https)')}
              </Label>
              <Input
                id='nd-dash'
                type='url'
                value={form.dashboard_url}
                placeholder='https://…'
                onChange={(e) => set({ dashboard_url: e.target.value })}
              />
            </div>
            <div className='sm:col-span-2'>
              <Label htmlFor='nd-mcp'>{t('nodeDirectory.fieldMcp', 'URL MCP (volitelné)')}</Label>
              <Input
                id='nd-mcp'
                type='url'
                value={form.mcp_url}
                placeholder='https://…'
                onChange={(e) => set({ mcp_url: e.target.value })}
              />
            </div>
            <div className='sm:col-span-2'>
              <Label htmlFor='nd-node'>
                {t('nodeDirectory.fieldNodeId', 'UUID uzlu (volitelné)')}
              </Label>
              <Input
                id='nd-node'
                value={form.node_id}
                onChange={(e) => set({ node_id: e.target.value })}
              />
            </div>
            <div className='sm:col-span-2'>
              <Label htmlFor='nd-notes'>{t('nodeDirectory.fieldNotes', 'Poznámka')}</Label>
              <Textarea
                id='nd-notes'
                rows={2}
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </div>
            <p className='text-[11px] text-gray-500 sm:col-span-2'>
              {t(
                'nodeDirectory.formHint',
                'Jen https a doménové jméno (ne IP). Adresář se sdílí v clusteru, na zákaznické uzly se nešíří.',
              )}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
